/**
 * LEGACY-ONLY. Renderiza el PDF de mantenciones con design_version != 'lowcode'
 * (las creadas antes de la migración a pdf_templates). Las mantenciones nuevas
 * usan InformeMantencionDownload (lowcode). No borrar: es el renderer oficial
 * del histórico. Ver thoughts/plans/2026-06-20-0001-informe-mantencion-...
 */
import React, { useEffect, useState } from 'react';
import { PDFViewer, PDFDownloadLink } from '@react-pdf/renderer';
import { useMediaQuery } from 'react-responsive';
import MantencionDocument from './MantencionDocument';
import { Button, Divider, Form } from 'antd';
import { HistoryOutlined } from '@ant-design/icons';
import { getAccessTokenApi } from 'api/auth';
import { getStaticMantencionData } from 'api/budget';
import useGenerateAndSendPDF from './useMantencionSend';
import useAuth from 'hooks/useAuth';
import usePerfilActivo from 'hooks/usePerfilActivo';
import useSignatureUrls from 'hooks/useSignatureUrls';
import EmailRecipients from 'components/global/Form/EmailRecipients';
import DocumentDeliveryDrawer from 'components/global/DocumentDeliveryDrawer';

function MantencionDownload({ mantencion }) {
  const { Ascensores, mantencion: mantencionData } = mantencion;
  const { generateAndSendPDF, loading: isLoadingSendPDF } = useGenerateAndSendPDF();
  const { urls: signatureUrls, ready: signaturesReady } = useSignatureUrls({
    firma_spec: mantencionData?.firma_spec,
    firma_recep: mantencionData?.firma_recep,
  });
  const mantencionForPDF = signaturesReady
    ? {
        ...mantencionData,
        firma_spec: signatureUrls.firma_spec,
        firma_recep: signatureUrls.firma_recep,
      }
    : mantencionData;

  // Detect if the device is a mobile device
  const isMobile = useMediaQuery({ maxWidth: 767 });
  const { user } = useAuth();
  const { perfilActivo } = usePerfilActivo();

  const [config, setConfig] = useState({});
  const [staticData, setStaticData] = useState(null);
  const [historialOpen, setHistorialOpen] = useState(false);
  const token = getAccessTokenApi();
  // Bandera de carga: `staticData` arranca en null y el documento se arma igual,
  // asi que sin esto el boton de descarga (y peor, el de enviar correo) quedan
  // disponibles con los datos del tenant todavia en vuelo.
  const [staticReady, setStaticReady] = useState(false);
  useEffect(() => {
    let cancelled = false;
    setStaticReady(false);
    getStaticMantencionData(token)
      .then((result) => {
        if (cancelled) return;
        setStaticData(result.static);
        setConfig(result.tenant);
      })
      // `finally` y no `then`: si el fetch falla, los botones se habilitan igual
      // en vez de quedar colgados para siempre.
      .catch((err) => {
        // Sin este catch la cadena termina en un unhandled rejection cuando el
        // fetch falla. El documento se arma sin este dato, pero el boton se
        // habilita igual (ver el `finally`) para no dejarlo colgado.
        console.error('[PDF] No se pudo cargar un dato del documento:', err);
      })
      .finally(() => {
        if (!cancelled) setStaticReady(true);
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  const onFinish = (values) => {
    generateAndSendPDF(mantencion, staticData, { to: values.to, cc: values.cc }, mantencionData.id);
  };

  return (
    <div>
      {!isMobile && (
        <PDFViewer
          style={{
            width: '100%',
            height: '80vh',
          }}
        >
          <MantencionDocument
            staticData={staticData}
            Ascensores={Ascensores}
            mantencion={mantencionForPDF}
            config={config}
            actividades={mantencion.actividades}
          />
        </PDFViewer>
      )}
      <div style={{ marginTop: '20px', textAlign: 'center' }}>
        {/* El `loading` de PDFDownloadLink solo dice si termino de ARMAR el PDF,
            no si los datos llegaron: con el fetch en vuelo el documento se arma
            vacio, se genera rapidisimo y el boton queda habilitado. */}
        {!staticReady ? (
          <Button type="primary" disabled block>
            Cargando datos...
          </Button>
        ) : (
          <PDFDownloadLink
            document={
              <MantencionDocument
                staticData={staticData}
                Ascensores={Ascensores}
                mantencion={mantencionForPDF}
                actividades={mantencion.actividades}
                config={config}
              />
            }
            fileName={'Informe de mantención N°' + mantencionData.id}
          >
            {({ loading }) => (
              <Button type="primary" disabled={loading} block>
                {loading ? 'Generando documento...' : 'Descargar PDF'}
              </Button>
            )}
          </PDFDownloadLink>
        )}
      </div>
      {(perfilActivo ?? user.type) === 'ejecutivo' && (
        <div>
          <Divider>Para enviar Correo</Divider>
          <Form
            onFinish={onFinish}
            layout="vertical"
            initialValues={{ to: [mantencionData?.Cliente?.email].filter(Boolean), cc: [] }}
          >
            <EmailRecipients />
            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                loading={isLoadingSendPDF}
                disabled={!staticReady}
                block
              >
                Enviar Correo
              </Button>
            </Form.Item>
          </Form>
          <Button
            icon={<HistoryOutlined />}
            block
            onClick={() => setHistorialOpen(true)}
            style={{ marginTop: 8 }}
          >
            Historial de entregas
          </Button>
        </div>
      )}
      <DocumentDeliveryDrawer
        open={historialOpen}
        onClose={() => setHistorialOpen(false)}
        tipo="mantencion"
        entidadId={mantencionData?.id}
        titulo={`Mantención #${mantencionData?.id}`}
      />
    </div>
  );
}

export default MantencionDownload;
